import { Col, Row } from 'reactstrap';
import {
  IDENTITIES,
  REQUESTS_REQUIRE_COMMENT,
  PRIVACY_REQUESTS,
  DATA_PRIVACY_FORM_FIELDS,
  REQUESTS_TYPES,
} from 'site-modules/data-privacy-request/constants/constants';
import React, { Fragment, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { StyledSelect } from 'site-modules/shared/components/styled-select/styled-select';
import { DataPrivacyFormField } from 'site-modules/data-privacy-request/components/data-privacy-form-field/data-privacy-form-field';
import { DataPrivacyRequestContent } from 'site-modules/data-privacy-request/components/data-privacy-request-content/data-privacy-request-content';
import { PHONE_MASK_V2 } from 'site-modules/shared/constants/lead-form/validation';
import { CmsEntities } from 'client/data/models/cms';
import { requestTypesDescriptionsSelector } from 'site-modules/data-privacy-request/utils/cms-content-selectors';
import { FormFieldMask } from 'site-modules/shared/components/form-field-mask/form-field-mask';

const getRequestTypeDescription = (requestType, texts, isConsumerAgent) => {
  let requestTypeDescriptionMap = {
    [PRIVACY_REQUESTS.CCPA_GET_CATEGORIES_PI]: texts['get-categories-pi'],
    [PRIVACY_REQUESTS.CCPA_GET_SPECIFIC_PI]: texts['get-specific-pi'],
    [PRIVACY_REQUESTS.CCPA_DELETE_PI]: texts['delete-pi'],
    [PRIVACY_REQUESTS.REQUEST_CORRECTION]: texts['request-correction'],
    [PRIVACY_REQUESTS.APPEAL_REQUEST]: texts['appeal-request'],
  };

  if (isConsumerAgent) {
    requestTypeDescriptionMap = {
      [PRIVACY_REQUESTS.CCPA_GET_CATEGORIES_PI]: texts['get-categories-pi-agent'],
      [PRIVACY_REQUESTS.CCPA_GET_SPECIFIC_PI]: texts['get-specific-pi-agent'],
      [PRIVACY_REQUESTS.CCPA_DELETE_PI]: texts['delete-pi-agent'],
      [PRIVACY_REQUESTS.REQUEST_CORRECTION]: texts['request-correction-agent'],
      [PRIVACY_REQUESTS.APPEAL_REQUEST]: texts['appeal-request-agent'],
    };
  }

  return requestTypeDescriptionMap[requestType];
};

export const DataPrivacyRequestConsumerFieldSet = ({
  identity,
  fieldRef,
  validationErrors = {},
  onValidate,
  cmsContent,
  stateCodes = [],
}) => {
  const [requestType, setRequestType] = useState();
  const [consumerState, setConsumerState] = useState();

  const isConsumerAgent = identity === IDENTITIES.AUTHORIZED_AGENT;
  const labelName = isConsumerAgent ? `Consumer's` : 'Your';

  const handleRequestTypeChange = useCallback(selectedType => {
    setRequestType(selectedType.value);
  }, []);

  const handleConsumerStateChange = useCallback(selectedState => {
    setConsumerState(selectedState.stateCode);
  }, []);

  return (
    <Fragment>
      <hr />
      <fieldset>
        <legend className="visually-hidden">Customers info</legend>
        <div className="medium mb-1">Asterisks (*) indicate required fields.</div>
        <DataPrivacyFormField
          value={requestType}
          options={REQUESTS_TYPES}
          onChange={handleRequestTypeChange}
          onBlur={onValidate}
          id={DATA_PRIVACY_FORM_FIELDS.REQUEST_TYPE}
          name={DATA_PRIVACY_FORM_FIELDS.REQUEST_TYPE}
          valueKey="value"
          labelKey="label"
          labelText="Privacy Request Type*"
          labelClassName="ps-0_25"
          fieldRef={fieldRef}
          tag={StyledSelect}
          isFirstOptionDisabled
          toggle="Select an option"
          isValid={!(DATA_PRIVACY_FORM_FIELDS.REQUEST_TYPE in validationErrors)}
          required
          error={validationErrors[DATA_PRIVACY_FORM_FIELDS.REQUEST_TYPE]}
          formControlColor="text-gray-darkest"
        />
        <DataPrivacyRequestContent className="mb-1">
          {getRequestTypeDescription(requestType, requestTypesDescriptionsSelector(cmsContent), isConsumerAgent)}
        </DataPrivacyRequestContent>
        {REQUESTS_REQUIRE_COMMENT.has(requestType) && (
          <Fragment>
            <DataPrivacyFormField
              id={DATA_PRIVACY_FORM_FIELDS.REQUEST_DESCRIPTION}
              maxLength={2000}
              name={DATA_PRIVACY_FORM_FIELDS.REQUEST_DESCRIPTION}
              label="Describe Request*"
              labelClassName="ps-0_25"
              tag="textarea"
              placeholder={`Please provide a detailed description of your request.  If a prior Data Privacy Request was made that resulted in your${isConsumerAgent ? ' or the consumer’s' : ''} knowledge of incorrect information, please reference the request(s) in the description.`}
              fieldRef={fieldRef}
              withCharacterLimit
              required
              onBlur={onValidate}
              isValid={!(DATA_PRIVACY_FORM_FIELDS.REQUEST_DESCRIPTION in validationErrors)}
              error={validationErrors[DATA_PRIVACY_FORM_FIELDS.REQUEST_DESCRIPTION]}
            />
          </Fragment>
        )}
        <DataPrivacyFormField
          id={DATA_PRIVACY_FORM_FIELDS.CONSUMER_EMAIL}
          maxLength={255}
          name={DATA_PRIVACY_FORM_FIELDS.CONSUMER_EMAIL}
          size={20}
          type="email"
          placeholder="Email Address"
          label={`${labelName} Email Address*`}
          labelClassName="ps-0_25"
          fieldRef={fieldRef}
          isValid={!(DATA_PRIVACY_FORM_FIELDS.CONSUMER_EMAIL in validationErrors)}
          onBlur={onValidate}
          required
          error={validationErrors[DATA_PRIVACY_FORM_FIELDS.CONSUMER_EMAIL]}
          autoComplete="email"
        />
        <Row>
          <Col xs={6}>
            <DataPrivacyFormField
              id={DATA_PRIVACY_FORM_FIELDS.CONSUMER_FIRST_NAME}
              maxLength={35}
              name={DATA_PRIVACY_FORM_FIELDS.CONSUMER_FIRST_NAME}
              size={20}
              type="text"
              placeholder="First Name"
              label={`${labelName} First Name*`}
              labelClassName="ps-0_25"
              fieldRef={fieldRef}
              isValid={!(DATA_PRIVACY_FORM_FIELDS.CONSUMER_FIRST_NAME in validationErrors)}
              onBlur={onValidate}
              required
              error={validationErrors[DATA_PRIVACY_FORM_FIELDS.CONSUMER_FIRST_NAME]}
              autoComplete="given-name"
            />
          </Col>
          <Col xs={6}>
            <DataPrivacyFormField
              id={DATA_PRIVACY_FORM_FIELDS.CONSUMER_LAST_NAME}
              maxLength={35}
              name={DATA_PRIVACY_FORM_FIELDS.CONSUMER_LAST_NAME}
              size={20}
              type="text"
              placeholder="Last Name"
              label={`${labelName} Last Name*`}
              labelClassName="ps-0_25"
              fieldRef={fieldRef}
              isValid={!(DATA_PRIVACY_FORM_FIELDS.CONSUMER_LAST_NAME in validationErrors)}
              onBlur={onValidate}
              required
              error={validationErrors[DATA_PRIVACY_FORM_FIELDS.CONSUMER_LAST_NAME]}
              autoComplete="family-name"
            />
          </Col>
        </Row>
        <DataPrivacyFormField
          component={FormFieldMask}
          id={DATA_PRIVACY_FORM_FIELDS.CONSUMER_PHONE}
          name={DATA_PRIVACY_FORM_FIELDS.CONSUMER_PHONE}
          type="tel"
          label={`${labelName} Phone Number*`}
          labelClassName="ps-0_25"
          fieldRef={fieldRef}
          isValid={!(DATA_PRIVACY_FORM_FIELDS.CONSUMER_PHONE in validationErrors)}
          onBlur={onValidate}
          required
          error={validationErrors[DATA_PRIVACY_FORM_FIELDS.CONSUMER_PHONE]}
          mask={{ ...PHONE_MASK_V2, placeholder: 'Phone Number' }}
          autoComplete="tel"
        />
        <DataPrivacyFormField
          id={DATA_PRIVACY_FORM_FIELDS.CONSUMER_ADDRESS}
          maxLength={90}
          name={DATA_PRIVACY_FORM_FIELDS.CONSUMER_ADDRESS}
          size={20}
          type="text"
          placeholder="Street Address"
          label={`${labelName} Street Address*`}
          labelClassName="ps-0_25"
          fieldRef={fieldRef}
          isValid={!(DATA_PRIVACY_FORM_FIELDS.CONSUMER_ADDRESS in validationErrors)}
          onBlur={onValidate}
          required
          error={validationErrors[DATA_PRIVACY_FORM_FIELDS.CONSUMER_ADDRESS]}
          autoComplete="street-address"
        />
        <Row>
          <Col>
            <DataPrivacyFormField
              id={DATA_PRIVACY_FORM_FIELDS.CONSUMER_CITY}
              maxLength={45}
              name={DATA_PRIVACY_FORM_FIELDS.CONSUMER_CITY}
              size={20}
              type="text"
              placeholder="City"
              label={`${labelName} City*`}
              labelClassName="ps-0_25"
              fieldRef={fieldRef}
              isValid={!(DATA_PRIVACY_FORM_FIELDS.CONSUMER_CITY in validationErrors)}
              onBlur={onValidate}
              required
              error={validationErrors[DATA_PRIVACY_FORM_FIELDS.CONSUMER_CITY]}
              autoComplete="address-level2"
            />
          </Col>
          <Col xs={6} sm={4}>
            <DataPrivacyFormField
              value={consumerState}
              options={stateCodes}
              onChange={handleConsumerStateChange}
              id={DATA_PRIVACY_FORM_FIELDS.CONSUMER_STATE}
              name={DATA_PRIVACY_FORM_FIELDS.CONSUMER_STATE}
              valueKey="stateCode"
              labelKey="stateCode"
              labelText={`${labelName} State*`}
              labelClassName="ps-0_25"
              fieldRef={fieldRef}
              tag={StyledSelect}
              isValid={!(DATA_PRIVACY_FORM_FIELDS.CONSUMER_STATE in validationErrors)}
              isFirstOptionDisabled
              toggle="Select State"
              required
              error={validationErrors[DATA_PRIVACY_FORM_FIELDS.CONSUMER_STATE]}
              onBlur={onValidate}
              formControlColor="text-gray-darkest"
              autoComplete="address-level1"
            />
          </Col>
        </Row>
        <Row>
          <Col xs={8}>
            <DataPrivacyFormField
              id={DATA_PRIVACY_FORM_FIELDS.CONSUMER_ZIP}
              maxLength={10}
              name="uzip"
              size={20}
              type="text"
              placeholder="Zip Code"
              label={`${labelName} Zip Code*`}
              labelClassName="ps-0_25"
              fieldRef={fieldRef}
              isValid={!(DATA_PRIVACY_FORM_FIELDS.CONSUMER_ZIP in validationErrors)}
              onBlur={onValidate}
              required
              error={validationErrors[DATA_PRIVACY_FORM_FIELDS.CONSUMER_ZIP]}
              autoComplete="postal-code"
            />
          </Col>
        </Row>
      </fieldset>
    </Fragment>
  );
};

DataPrivacyRequestConsumerFieldSet.propTypes = {
  identity: PropTypes.string.isRequired,
  fieldRef: PropTypes.func.isRequired,
  validationErrors: PropTypes.shape({}),
  onValidate: PropTypes.func.isRequired,
  cmsContent: CmsEntities.Content.isRequired,
  stateCodes: PropTypes.arrayOf(PropTypes.shape({ stateCode: PropTypes.string })),
};
